import { autobind } from 'core-decorators';
/* eslint-disable max-len */
import { debounce } from 'lodash-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Alert from '/@/components/bayer/alert';
import CornDensityChart from '/@/components/corn-density-chart';
import CornDensityChartTable from '/@/components/corn-density-chart-table';
import CornDensityTable from '/@/components/corn-density-table';
import CornPlantingDensityCalculator from '/@/components/corn-planting-density-calculator';
import { CORN_DENSITY_LIMITS_CONFIG } from '/@/core/density';
import { CORN_NITROGEN_LIMITS_CONFIG } from '/@/core/fertilization';
import settings from '/@/core/settings';
import { _, _lazy } from '/@/locale';
import { updateActiveRecommendation } from '/@/store/recommendations/actions';
import { getUser } from '/@/store/session/selectors';
import { formatEnvironmentRange, isMobile } from '/@/utils';
import Select from '/@/v3/components/forms/select';
import styles from './styles.module.css';

const USER_FIELD_CONFIG = {
    userDensity: {
        fixed: { limits: CORN_DENSITY_LIMITS_CONFIG },
        variable: { limits: CORN_DENSITY_LIMITS_CONFIG },
        toUnit: 'toDensity',
        fromUnit: 'fromDensity',
    },
    userNitrogen: {
        fixed: { limits: CORN_NITROGEN_LIMITS_CONFIG },
        variable: { limits: CORN_NITROGEN_LIMITS_CONFIG },
    },
    userPlanterEfficiency: {
        fixed: { limits: { min: 0 } },
        variable: { limits: { min: 0, max: 30 } },
    },
    userArea: {
        fixed: { limits: { min: 0 } },
        variable: { limits: { min: 0 } },
        toUnit: 'toArea',
        fromUnit: 'fromArea',
    },
    userCollapsed: {
        fixed: {},
        variable: {},
    },
};
@connect(
    (state) => ({ user: getUser(state) }),
    (dispatch) =>
        bindActionCreators(
            {
                updateActiveRecommendation: updateActiveRecommendation,
            },
            dispatch
        )
)
@autobind
export default class CornDensity extends Component {
    static label = (props) => _lazy(props?.changeDensityTitle ? 'Densidad objetivo' : 'Densidad')();

    static icon = 'density';

    static contextTypes = {
        units: PropTypes.object,
        print: PropTypes.bool,
    };

    state = {
        userDensity: '',
        userNitrogen: '',
        userPlanterEfficiency: '',
        userArea: '',
        userCollapsed: true,
        selectedProducts: [],
        environmentType: 'fixed', // Default value should be 'fixed' so we can handle anonymous sessions.
    };

    componentDidMount() {
        const products = this.getProductRecommendations().filter(({ harvestDensity }) => Boolean(harvestDensity));
        if (products.length > 0) {
            this.setState({ selectedProducts: [products?.[0]?.id] });
        }
    }

    UNSAFE_componentWillMount() {
        this.setUserValuesFromProps(this.props);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.setUserValuesFromProps(nextProps);
    }

    getFieldLimits() {
        return Object.entries(USER_FIELD_CONFIG).reduce((limits, [field, config]) => {
            if (!config.limits) return limits;
            limits[field] = Object.entries(config.limits).reduce((result, [key, value]) => {
                result[key] = config.toUnit ? this.context.units[config.toUnit](value, 'corn') : value;
                return result;
            }, {});
            return limits;
        }, {});
    }

    setUserValuesFromProps(props) {
        const {
            cropConfig: { userDensity, userNitrogen, userPlanterEfficiency, userArea, userCollapsed },
        } = props;

        this.setState({
            userDensity: userDensity || '',
            userNitrogen: userNitrogen || '',
            userPlanterEfficiency: userPlanterEfficiency || '',
            userArea: userArea || '',
            userCollapsed: userCollapsed !== undefined ? userCollapsed : true,
        });
    }

    @debounce(2000)
    async setUserValue(field, value) {
        const { limits } = USER_FIELD_CONFIG[field][this.state.environmentType] || {};

        if (limits && value !== '') {
            const { min, max } = limits;
            if (typeof min === 'number' && value < min) value = min;
            if (typeof max === 'number' && value > max) value = max;
        }

        const newCrop = { ...this.props.recommendation.crops[0], [field]: value !== '' ? value : null };
        await this.props.updateActiveRecommendation({
            id: this.props.recommendation.id,
            status: this.props.recommendation.status,
            crops: [newCrop],
            field: this.props.recommendation.field,
            appInstance: this.props.appInstance.id,
        });
    }

    getChangeHandler(field, setter) {
        console.log('getChangeHandler', field, setter);
        return async (value) => {
            if (value !== '') {
                value = Number(value);

                // Convert back and forth to user units to match default precission config.
                if (value !== '') {
                    value = Number(value);

                    // Convert back and forth to user units to match default precission config.
                    const { toUnit, fromUnit } = USER_FIELD_CONFIG[field] || {};
                    if (toUnit && fromUnit) {
                        value = this.context.units[fromUnit](value, 'corn');
                        value = this.context.units[toUnit](value, 'corn').value;
                        value = this.context.units[fromUnit](value, 'corn');
                    }
                }

                if (this.state[field] !== value) {
                    this.setState({ [field]: value });
                    this.setUserValue(field, value);
                }
            }

            if (this.state[field] !== value) {
                this.setState({ [field]: value });
                this.setUserValue(field, value);
            }
        };
    }

    getProductRecommendations() {
        const products = this.props.productRecommendations.filter(({ harvestDensity }) => Boolean(harvestDensity));

        if (this.props.cornDensityGraphicValues) {
            const parsedProducts = products.map((product) => {
                const curveValues = this.props.cornDensityGraphicValues[product.id];
                if (!curveValues) return product;
                if (!curveValues.selected) return product;

                let userSelectedDensityPoint;
                if (this.props.cropConfig.userDensity) {
                    userSelectedDensityPoint = curveValues.selected.points.reduce(
                        (closestPoint, point) => {
                            const diff = Math.abs(point.density * 10000 - this.props.cropConfig.userDensity);
                            if (!closestPoint.point)
                                return {
                                    point,
                                    diff,
                                };
                            if (diff < closestPoint.diff) {
                                return {
                                    point,
                                    diff,
                                };
                            }
                            return closestPoint;
                        },
                        { point: undefined, diff: undefined }
                    ).point;
                }

                let userSelectedDensityYield;
                let userHarvestDensityDifferential;
                if (userSelectedDensityPoint) {
                    userSelectedDensityYield = userSelectedDensityPoint.yield;
                    userHarvestDensityDifferential = {
                        value: curveValues.selected.recommended.yield - userSelectedDensityYield,
                        percentage:
                            (100 * (curveValues.selected.recommended.yield - userSelectedDensityYield)) /
                            curveValues.selected.recommended.yield,
                    };
                }
                return {
                    ...product,
                    estimatedYield: {
                        differentials: {
                            forUserHarvestDensity: userHarvestDensityDifferential,
                        },
                        forRecommendedHarvestDensity: curveValues.selected.recommended.yield,
                        forUserHarvestDensity: userSelectedDensityYield,
                        forBiologicalHarvestDensity:
                            curveValues.selected.biological && curveValues.selected.biological.yield,
                        fitRatio: curveValues.selected.fitRatio,
                    },
                    estimatedYieldByHarvestDensity: curveValues.selected.points.map(({ density, yield: _yield }) => ({
                        density: density * 10000,
                        estimatedYield: _yield,
                    })),
                    harvestDensity: {
                        recommended: curveValues.selected.recommended.density * 10000,
                        biological: curveValues.selected.biological && curveValues.selected.biological.density * 10000,
                        user: this.props.cropConfig.userDensity,
                    },
                };
            });
            return parsedProducts;
        }
        return products;
    }

    getProductsForDensityTable() {
        return this.props.productRecommendations.filter(({ profile }) => profile.densityTableValues.length > 0);
    }

    getVisibleProductRecommendations() {
        const { print } = this.context;
        const { selectedProducts, environmentType } = this.state;

        const productRecommendations = this.getProductRecommendations().map((productRecommendation) => ({
            ...productRecommendation,
            selected: print || selectedProducts.includes(productRecommendation.id),
        }));

        if (environmentType === 'fixed') {
            if (isMobile()) return productRecommendations.filter(({ selected }) => selected);
            return productRecommendations;
        }

        const selectedProductRecommendation = productRecommendations.find(
            ({ selected, estimatedYieldByHarvestDensity }) => selected && estimatedYieldByHarvestDensity
        );

        const { ...forSelectedEnvironment } = selectedProductRecommendation;
        return [{ ...forSelectedEnvironment, type: 'selected' }];
    }

    handleProductToggle(id) {
        this.setState((prevState) => {
            if (prevState.selectedProducts.includes(id)) {
                if (prevState.selectedProducts.length > 1) {
                    return { selectedProducts: prevState.selectedProducts.filter((selectedId) => selectedId !== id) };
                }
            } else {
                return { selectedProducts: [...prevState.selectedProducts, id] };
            }
        });
    }

    handleProductSelection(id) {
        this.setState({ selectedProducts: [id] });
    }

    handleEnvironmentTypeChange(environmentType) {
        this.setState((oldState) => ({
            ...oldState,
            environmentType,
            userArea:
                oldState.userArea !== ''
                    ? USER_FIELD_CONFIG.userArea?.[environmentType]?.limits.max || oldState.userArea
                    : '',
            userDensity:
                oldState.userBetterEnvironment !== ''
                    ? USER_FIELD_CONFIG.userDensity?.[environmentType]?.limits.max || oldState.userDensity
                    : '',
            userNitrogen:
                oldState.userNitrogen !== ''
                    ? USER_FIELD_CONFIG.userNitrogen?.[environmentType]?.limits.max || oldState.userNitrogen
                    : '',
            userPlanterEfficiency:
                oldState.userPlanterEfficiency !== ''
                    ? USER_FIELD_CONFIG.userPlanterEfficiency?.[environmentType]?.limits.max ||
                      oldState.userPlanterEfficiency
                    : '',
            selectedProducts: [
                this.getVisibleProductRecommendations().find(
                    ({ estimatedYieldByHarvestDensity }) => estimatedYieldByHarvestDensity
                ).id,
            ],
        }));
    }

    renderPlantingDensityCalculator(productRecommendations, selectedProductRecommendation) {
        const { print } = this.context;

        if (!this.props.appInstance.enableCornPlantingCalculator || !selectedProductRecommendation) return;

        const selectedHasBags = !!selectedProductRecommendation.profile.product.seedsPerBag;
        const recommendationsWithBags = productRecommendations.filter(
            ({ profile: { product } }) => !!product.seedsPerBag
        );
        if (recommendationsWithBags.length === 0 || (isMobile() && !selectedHasBags)) return;

        if (print && this.state.userCollapsed) return;

        return (
            <CornPlantingDensityCalculator
                brand={this.props.brand}
                productRecommendations={recommendationsWithBags}
                selectedProductRecommendation={selectedProductRecommendation}
                userPlanterEfficiency={this.state.userPlanterEfficiency}
                onUserPlanterEfficiencyChange={this.getChangeHandler(
                    'userPlanterEfficiency',
                    'setUserPlanterEfficiency'
                )}
                userArea={this.state.userArea}
                onUserAreaChange={this.getChangeHandler('userArea', 'setUserArea')}
                collapsed={this.state.userCollapsed !== false}
                onUserCollapsedChange={(value) => this.setUserValue('userCollapsed', value, 'setUserCollapsed')}
                status={this.props.recommendation.status}
            />
        );
    }

    renderReference(selectedProductRecommendation) {
        const { environmentType } = this.state;
        const { productRecommendations } = this.props;

        const showReference =
            environmentType === 'variable'
                ? selectedProductRecommendation.profile.preliminar
                : productRecommendations.some(({ profile: { preliminar } }) => !!preliminar);

        if (!showReference) return null;

        return (
            <div className={styles.reference}>
                <div className={styles.title}>{_('Referencias')}</div>
                <div className={styles.content}>
                    <ul>
                        <li>
                            *{' '}
                            {_(
                                'Debido a que es un producto de lanzamiento, este híbrido muestra datos de recomendación preliminar.'
                            )}
                        </li>
                    </ul>
                </div>
            </div>
        );
    }

    render() {
        const { print } = this.context;
        const { cropConfig, brand, user, productRecommendations } = this.props;
        const { environmentType } = this.state;

        const mobile = isMobile();

        const visibleProductRecommendations = this.getVisibleProductRecommendations();
        const productsWithDensityTableData = this.getProductsForDensityTable();

        const productsWithCoefficients = visibleProductRecommendations.filter(
            (p) => p.estimatedYieldByHarvestDensity && p.estimatedYieldByHarvestDensity.length > 0
        );

        const productsForDensityTable = productsWithDensityTableData.map((p) => {
            return {
                label: p.profile.label,
                densityTableValues: p.profile.densityTableValues.map((el) => ({
                    environment: formatEnvironmentRange({ min: el.minEnvironment, max: el.maxEnvironment }, (value) =>
                        this.context.units.toYield(value, 'corn')
                    ),
                    value: this.context.units.toCornDensity(el.value, 2),
                })),
            };
        });

        const allProductsIds = new Set([
            ...visibleProductRecommendations.map((p) => p.id),
            ...productsWithDensityTableData.map((p) => p.id),
        ]);

        const thereIsAProductWithouthCoefficients =
            allProductsIds.size >
            visibleProductRecommendations.filter((p) => Boolean(p.estimatedYieldByHarvestDensity)).length;

        const selectedProductRecommendation = visibleProductRecommendations.find(({ selected }) => selected);
        const productRecommendationsOptions = productRecommendations
            .filter(
                ({ harvestDensity, estimatedYieldByHarvestDensity }) =>
                    Boolean(harvestDensity) &&
                    Boolean(estimatedYieldByHarvestDensity) &&
                    estimatedYieldByHarvestDensity.length > 0
            )
            .map((productRecommendation) => ({
                value: productRecommendation.id,
                label: productRecommendation.profile.label,
            }));

        return (
            <div className={styles.root} style={{ minHeight: `${this.props.availableHeight}px` }}>
                <p>
                    {_(
                        'La densidad recomendada es la cantidad mínima de plantas a cosecha para lograr el máximo rendimiento en tu ambiente definido.'
                    )}
                </p>
                {productsWithCoefficients.length > 0 && (
                    <Fragment>
                        {!print &&
                            (mobile || environmentType === 'variable') &&
                            productRecommendationsOptions.length > 1 && (
                                <div>
                                    <Select
                                        value={selectedProductRecommendation.id}
                                        options={productRecommendationsOptions}
                                        onChange={this.handleProductSelection}
                                        clearable={false}
                                        appearance='outline'
                                        placeholder={_('Producto')}
                                        persistPlaceholder
                                        color={settings.app.theme}
                                    />
                                </div>
                            )}
                        {brand.showDensityChart && (
                            <div className={styles.chart}>
                                <CornDensityChart
                                    key={environmentType}
                                    products={visibleProductRecommendations}
                                    importantReferences={environmentType === 'variable'}
                                />
                            </div>
                        )}
                    </Fragment>
                )}

                {brand.showDensityChart &&
                    (thereIsAProductWithouthCoefficients || visibleProductRecommendations.length === 0) && (
                        <Alert severity='danger'>
                            {productsForDensityTable.length > 0
                                ? _(
                                      'Existen algunos productos sin suficientes años de datos para construir la curva de densidad. Consulte los siguientes rangos sugeridos.'
                                  )
                                : _(
                                      'Existen algunos productos sin suficientes años de datos para construir la curva de densidad.'
                                  )}
                        </Alert>
                    )}
                {brand.showDensityChart && productsForDensityTable.length > 0 && (
                    <CornDensityChartTable products={productsForDensityTable} />
                )}
                <div className={styles.table}>
                    <CornDensityTable
                        user={user}
                        cropConfig={cropConfig}
                        fieldLimits={this.getFieldLimits()}
                        productRecommendations={visibleProductRecommendations}
                        selectedProductRecommendation={selectedProductRecommendation}
                        environmentType={environmentType}
                        onProductToggle={this.handleProductToggle}
                        userDensity={this.state.userDensity}
                        onUserDensityChange={this.getChangeHandler('userDensity', 'setUserDensity')}
                        userNitrogen={this.state.userNitrogen}
                        onUserNitrogenChange={this.getChangeHandler('userNitrogen', 'setUserNitrogen')}
                        status={this.props.recommendation.status}
                    />
                </div>
                {this.renderReference(selectedProductRecommendation)}
                {this.renderPlantingDensityCalculator(this.getProductRecommendations(), selectedProductRecommendation)}
            </div>
        );
    }
}
